import { SelectInput, SelectOption, Stack } from "@ucchub/ui";
import Paragraph from "@vfuk/core-paragraph/";
import Divider from "@vfuk/core-divider";
import Heading from "@vfuk/core-heading";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import SimpleGrid from "@vfuk/core-simple-grid";
import Spacing from "@vfuk/core-spacing";
import Button from "@vfuk/core-button/";
import StateNotification from "@vfuk/core-state-notification";
import { BillingContextListResponseV1 } from "@ucchub/ui/src/apiTypes/cloudNumbers";
import { CommercialCriteria } from "@components/customer/CommercialCriteria.types";
import { Country } from "@components/country/Country.types";
import CommercialCriteriaTable from "./CommercialCriteriaTable";
import { Checkbox } from "@vfuk/core-checkbox";
import { useEffect, useState } from "react";
import RadioButtonList from "@vfuk/core-radio-button-list";
import {
  AddBillingContextFormRequest,
  AddBillingContextFormValues,
} from "@components/customer/BillingContext.types";
import { ServiceType } from "@components/CommercialCriteria/ServiceType.types";

export type BillingContextConfigFormProps = {
  onSubmit: (data: AddBillingContextFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error?: string;
  accountConfigToEdit?: BillingContextListResponseV1;
  commercialCriteria: CommercialCriteria[];
  countryList: Country[];
  accessTypeList: ServiceType[];
  commercialId: string;
  billingContextList: BillingContextListResponseV1[];
};

const BillingContextConfigForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
  error,
  commercialCriteria,
  countryList,
  accessTypeList,
  commercialId,
  billingContextList,
}: BillingContextConfigFormProps) => {
  const { t } = useTranslation();
  const simpleGridInputLayout = { sm: [4.5, 4.5] };

  const {
    handleSubmit,
    control,
    getValues,
    reset,
    resetField,
    watch,
    formState: { isDirty, isValid },
  } = useForm<AddBillingContextFormRequest>({
    shouldFocusError: false,
    mode: "onTouched",
  });
  const [checked, setChecked] = useState("0");

  const items = [
    {
      label: t("core:billingContext:add:start"),
      value: "0",
    },
    {
      label: t("core:billingContext:add:end"),
      value: "1",
    },
  ];
  const [hasAcceptedBilling, setAcceptedBilling] = useState(false);
  const [isAccessTypeExist, setIsAccessTypeExist] = useState(false);
  const [selectedCommercialCriteria, setSelectedCommercialCriteria] =
    useState<CommercialCriteria>();
  const disabledState =
    isSubmitting || isAccessTypeExist ? "disabled" : undefined;
  const saveBtnDisabledState =
    !isValid || !isDirty || !hasAcceptedBilling ? "disabled" : disabledState;

  useEffect(() => {
    reset();
    setAcceptedBilling(false);
    setChecked("0");
  }, []);

  const handleOnSubmit = () => {
    const dataPayload = getValues();

    const payload: AddBillingContextFormValues = {
      commercialCriteriaId: dataPayload.commercialCriteriaId?.value,
      accessTypeId: dataPayload.accessTypeId?.value,
      isLocationEnd: checked === "1" ? true : false,
      alternativeCountryId: dataPayload.alternativeCountryId?.value,
      isCheckboxChecked: hasAcceptedBilling,
      countryId: selectedCommercialCriteria?.countryId || "",
    };
    if (!saveBtnDisabledState) {
      onSubmit(payload);
    }
  };

  const handleCommercialCriteriaChange = (option: SelectOption) => {
    const cCriteriaList = commercialCriteria.find(
      ({ id }) => id === option.value
    );
    setSelectedCommercialCriteria(cCriteriaList);
  };

  const AccessTypeIsExist = (
    commercialCriteria: string,
    accessType: string
  ) => {
    const BillingContext = billingContextList.some(
      (billingContext) =>
        billingContext.commercialCriteriaId === commercialCriteria &&
        billingContext.accessTypeId === accessType
    );
    setIsAccessTypeExist(BillingContext ? true : false);
  };

  useEffect(() => {
    if (watch("accessTypeId") && watch("commercialCriteriaId")) {
      AccessTypeIsExist(
        watch("commercialCriteriaId")?.value || "",
        watch("accessTypeId")?.value || ""
      );
    }
  }, [watch("accessTypeId"), watch("commercialCriteriaId")]);

  return (
    <>
      <Heading
        level={4}
        weight={3}
        noMargin
        text={t("core:billingContext:add:primary_header") || undefined}
      />
      <Paragraph size={2}>
        {t("core:billingContext:add:secondary_header")}
      </Paragraph>
      {error && (
        <Spacing spacingLevel={{ top: 4 }}>
          <StateNotification state="error">
            <Paragraph>{error}</Paragraph>
          </StateNotification>
        </Spacing>
      )}
      <Spacing spacingLevel={{ top: 6, bottom: 7 }}>
        <Divider appearance="secondary" />
      </Spacing>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Spacing spacingLevel={{ bottom: 3 }}>
          <SimpleGrid layout={{ sm: [12], lg: [12] }}>
            <Paragraph weight={3} noMargin>
              {t("core:billingContext:add:customer_commercial_id")}
            </Paragraph>
            <Paragraph>{commercialId}</Paragraph>
          </SimpleGrid>
        </Spacing>
        <SimpleGrid layout={simpleGridInputLayout}>
          <Controller
            name="commercialCriteriaId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SelectInput
                label={t("core:billingContext:add:commercial_criteria_name")}
                id="commercialCriteriaId"
                options={commercialCriteria?.map((cCriteria) => ({
                  value: cCriteria.id,
                  label: cCriteria.name,
                }))}
                {...field}
                onChange={(option: SelectOption) => {
                  field.onChange(option);
                  handleCommercialCriteriaChange(option);
                }}
              />
            )}
          />
        </SimpleGrid>
        {selectedCommercialCriteria && (
          <SimpleGrid layout={{ sm: [12], lg: [12] }}>
            <CommercialCriteriaTable
              commercialCriteria={selectedCommercialCriteria}
              countryList={countryList}
            />
          </SimpleGrid>
        )}
        <Spacing spacingLevel={{ top: 1 }}>
          <SimpleGrid layout={simpleGridInputLayout} spacing={1}>
            <Controller
              name="accessTypeId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectInput
                  label={t("core:billingContext:add:accessType")}
                  id="accessTypeId"
                  options={accessTypeList?.map((accessType) => ({
                    value: accessType.id,
                    label: accessType.name,
                  }))}
                  {...field}
                  error={
                    isAccessTypeExist
                      ? t("core:billingContext:accessType_exists") || undefined
                      : undefined
                  }
                />
              )}
            />
          </SimpleGrid>
        </Spacing>
        <SimpleGrid layout={{ sm: [12, 12], lg: [5, 4] }} spacing={4}>
          <RadioButtonList
            fieldWrapper={{
              label: t("core:billingContext:add:select_location_of_country"),
            }}
            orientation="horizontal"
            radioButtons={{
              groupName: "radio-buttons",
              id: "radio-buttons",
              checked,
              onChange: (event) => {
                setChecked(event.target.value);
                resetField("alternativeCountryId");
              },
              items,
            }}
          />
          {checked === "1" && (
            <Spacing spacingLevel={{ top: 1 }}>
              <Controller
                name="alternativeCountryId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectInput
                    label={t("core:billingContext:add:countryCode")}
                    id="alternativeCountryId"
                    options={countryList?.map((countryId) => ({
                      value: countryId.id,
                      label: "+" + countryId.countryCode + " " + countryId.name,
                    }))}
                    {...field}
                  />
                )}
              />
            </Spacing>
          )}
        </SimpleGrid>
        <Spacing spacingLevel={{ top: 3 }}>
          <Checkbox
            id="isCheckboxChecked"
            name="isCheckboxChecked"
            value=""
            checked={hasAcceptedBilling || false}
            onChange={(option) => {
              setAcceptedBilling(option.target.checked);
            }}
          >
            {t("core:billingContext:add:check_box")}
          </Checkbox>
        </Spacing>
        <Spacing spacingLevel={{ top: 4, bottom: 4 }}>
          <Divider appearance="secondary" />
        </Spacing>
        <Stack columnGap="1rem" justifyContent="flex-end">
          <Button
            text={t("core:accountConfig:cancel")}
            appearance="alt1"
            onClick={onCancel}
            state={isSubmitting ? "disabled" : undefined}
          />
          <Button
            onClick={() => null}
            text={t("core:accountConfig:submit")}
            appearance="primary"
            htmlAttributes={{ type: "submit" }}
            loading={isSubmitting}
            state={saveBtnDisabledState}
          />
        </Stack>
      </form>
    </>
  );
};

export default BillingContextConfigForm;
